/**
 * Product Media Management System - Moon Moon Agency
 * Handles media display and slider interactions
 */

/*********************************************************************************************
 * CONFIGURATION
 */
const MEDIA_CONFIG = {
	selectors: {
		productMedias: '[data-product-medias] [data-splide-slide]',
		mainSlider: '[data-main-slider]',
		dataId: '[data-id="{{id}}"]'
	},

	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * UTILITIES & LOGGING
 */
const mediaLog = (level, message, data = {}) => {
	if (!MEDIA_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`[MEDIA] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

const mediaEmit = (event, detail = {}, cancelable = false) => {
	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: {
			timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
			event,
			data: detail,
			source: 'media'
		},
		cancelable,
		bubbles: true
	}));
};

/*********************************************************************************************
 * MEDIA MANAGEMENT
 */
const MediaManager = {
	updateMedia(variant) {
		if (!variant?.featured_media?.id) return;

		try {
			const variantMediaId = variant.featured_media.id;
			const allMainMedia = document.querySelectorAll(MEDIA_CONFIG.selectors.productMedias);

			if (!allMainMedia.length) {
				mediaLog('info', 'No media slides found');
				return;
			}

			const targetSlideIndex = Array.from(allMainMedia).findIndex(media => {
				const mediaId = media.dataset.id;
				return mediaId && parseInt(mediaId, 10) === variantMediaId;
			});

			if (targetSlideIndex !== -1) {
				if (window.splideMainInstance && typeof window.splideMainInstance.go === 'function') {
					window.splideMainInstance.go(targetSlideIndex);
				} else {
					mediaLog('info', 'Splide instance not available or invalid');
				}
			} else {
				mediaLog('info', 'Target slide not found for media ID', { variantMediaId });
			}
		} catch (error) {
			mediaLog('error', 'Failed to update media', { error: error.message });
		}
	},

	/**
	 * Navigate to the variant's featured media in the slider
	 */
	updateImagesVariant(variant) {
		if (!variant?.id) return;

		const variantId = variant.id.toString();
		mediaEmit('variant-media-switch-start', { variantId });

		try {
			if (!variant.featured_media?.id) {
				mediaEmit('variant-media-switch-success', { variantId, type: 'no-featured-media' });
				return;
			}

			this.updateMedia(variant);

			const featuredMediaId = variant.featured_media.id;
			const mainSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSlider);

			if (mainSlider) {
				const targetSlide = mainSlider.querySelector(MEDIA_CONFIG.selectors.dataId.replace('{{id}}', featuredMediaId));
				if (targetSlide) {
					const slideIndex = parseInt(targetSlide.dataset.index, 10);
					if (!isNaN(slideIndex)) {
						const sliderPair = window.ProductMediaSliders?.instances?.['main'];
						if (sliderPair?.main?.go) {
							sliderPair.main.go(slideIndex);
							mediaEmit('variant-media-switch-success', { variantId, type: 'featured-media-navigation', slideIndex });
							return;
						}
					}
				}
			}

			mediaEmit('variant-media-switch-success', { variantId, type: 'standard-navigation' });
		} catch (error) {
			mediaLog('error', 'Failed to navigate to featured media', { error: error.message });
			mediaEmit('variant-media-switch-error', { variantId, error: error.message });
		}
	}
};

/*********************************************************************************************
 * GLOBAL ACCESS
 */
window.MediaManager = MediaManager;

/*********************************************************************************************
 * DEVELOPMENT LOGGING
 */
if (MEDIA_CONFIG.isDevelopment) {
	['variant-media-switch-start', 'variant-media-switch-success', 'variant-media-switch-error'].forEach(event => {
		document.addEventListener(`product:${event}`, e =>
			console.log(`[MEDIA] ${event}:`, e.detail)
		);
	});
}